// Employee Engagement Budget Calculator Entry Point
// Import Highcharts and useful modules for charts FIRST so dependent code can use it
import Highcharts from 'highcharts';
import 'highcharts/modules/exporting';
import 'highcharts/modules/export-data';

// Global chart options for visual consistency
Highcharts.setOptions({
  chart: {
    style: { fontFamily: 'inherit' }
  },
  colors: [
    '#28a745', // success
    '#ffc107', // warning
    '#17a2b8', // info
    '#dc3545', // danger
    '#6c757d', // secondary
    '#343a40', // dark
    '#007bff'  // primary
  ]
});

class EmployeeEngagementBudgetCalculator {
  constructor() {
    this.charts = {};
    this.init();
  }

  init() {
    this.bindEvents();
    this.setDefaultValues();
  }

  bindEvents() {
    $('#calculate-budget').on('click', () => this.calculateBudget());
    $('#reset-calculator').on('click', () => this.resetCalculator());
    
    // Auto-calculate on input changes
    $('input, select').on('input change', () => {
      if (this.hasValidInputs()) {
        this.calculateBudget();
      }
    });
  }

  setDefaultValues() {
    // Set some reasonable defaults
    $('#budget-per-employee').val(500);
    $('#wellness-gym-memberships').val(25);
    $('#wellness-health-screenings').val(150);
    $('#wellness-nutrition').val(15);
    $('#wellness-mental-health').val(30);
    $('#recognition-points-budget').val(50);
    $('#recognition-anniversary').val(100);
    $('#recognition-awards').val(75);
    $('#recognition-celebration-events').val(200);
    $('#mentorship-platform').val(10);
    $('#mentorship-training').val(500);
    $('#mentorship-meetings').val(25);
    $('#mentorship-resources').val(50);
    $('#events-holiday-party').val(150);
    $('#events-team-building').val(75);
    $('#events-company-picnic').val(100);
    $('#events-other').val(200);
  }

  hasValidInputs() {
    const employeeCount = parseInt($('#employee-count').val()) || 0;
    return employeeCount > 0;
  }

  calculateBudget() {
    if (!this.hasValidInputs()) {
      alert('Please enter the number of employees to calculate the budget.');
      return;
    }

    const employeeCount = parseInt($('#employee-count').val());
    const budgetPerEmployee = parseFloat($('#budget-per-employee').val()) || 0;

    // Calculate wellness programs
    const wellnessGymMemberships = parseFloat($('#wellness-gym-memberships').val()) || 0;
    const wellnessHealthScreenings = parseFloat($('#wellness-health-screenings').val()) || 0;
    const wellnessNutrition = parseFloat($('#wellness-nutrition').val()) || 0;
    const wellnessMentalHealth = parseFloat($('#wellness-mental-health').val()) || 0;

    const wellnessAnnual = (wellnessGymMemberships * 12 + wellnessHealthScreenings + 
                           wellnessNutrition * 12 + wellnessMentalHealth * 12) * employeeCount;

    // Calculate recognition programs
    const recognitionPointsBudget = parseFloat($('#recognition-points-budget').val()) || 0;
    const recognitionAnniversary = parseFloat($('#recognition-anniversary').val()) || 0;
    const recognitionAwards = parseFloat($('#recognition-awards').val()) || 0;
    const recognitionCelebrationEvents = parseFloat($('#recognition-celebration-events').val()) || 0;

    const recognitionAnnual = (recognitionPointsBudget * 12 + recognitionAnniversary + 
                              recognitionAwards + recognitionCelebrationEvents) * employeeCount;

    // Calculate mentorship programs
    const mentorshipPlatform = parseFloat($('#mentorship-platform').val()) || 0;
    const mentorshipTraining = parseFloat($('#mentorship-training').val()) || 0;
    const mentorshipMeetings = parseFloat($('#mentorship-meetings').val()) || 0;
    const mentorshipResources = parseFloat($('#mentorship-resources').val()) || 0;

    // Assume 20% of employees are mentors
    const mentorCount = Math.ceil(employeeCount * 0.2);
    const mentorshipAnnual = (mentorshipPlatform * 12 * employeeCount) + 
                            (mentorshipTraining * mentorCount) + 
                            (mentorshipMeetings * 12 * mentorCount) + 
                            (mentorshipResources * employeeCount);

    // Calculate special events
    const eventsHolidayParty = parseFloat($('#events-holiday-party').val()) || 0;
    const eventsTeamBuilding = parseFloat($('#events-team-building').val()) || 0;
    const eventsCompanyPicnic = parseFloat($('#events-company-picnic').val()) || 0;
    const eventsOther = parseFloat($('#events-other').val()) || 0;

    // Assume 4 team building events per year
    const eventsAnnual = (eventsHolidayParty + eventsCompanyPicnic + eventsOther + 
                         eventsTeamBuilding * 4) * employeeCount;

    // Calculate totals
    const totalAnnualBudget = wellnessAnnual + recognitionAnnual + mentorshipAnnual + eventsAnnual;
    const perEmployeeBudget = totalAnnualBudget / employeeCount;
    const monthlyBudget = totalAnnualBudget / 12;
    const targetBudget = budgetPerEmployee * employeeCount;
    const budgetDifference = totalAnnualBudget - targetBudget;

    // Display results
    this.displayResults({
      wellnessAnnual,
      recognitionAnnual,
      mentorshipAnnual,
      eventsAnnual,
      totalAnnualBudget,
      perEmployeeBudget,
      monthlyBudget,
      targetBudget,
      budgetDifference,
      employeeCount
    });

    $('#results-section').show();

    // Create or update charts
    this.updateCharts({
      wellnessAnnual,
      recognitionAnnual,
      mentorshipAnnual,
      eventsAnnual,
      totalAnnualBudget
    });
  }

  displayResults(results) {
    const breakdownList = $('#budget-breakdown-list');
    breakdownList.empty();

    const categories = [
      { name: 'Wellness Programs', amount: results.wellnessAnnual, color: 'success' },
      { name: 'Recognition Programs', amount: results.recognitionAnnual, color: 'warning' },
      { name: 'Mentorship Programs', amount: results.mentorshipAnnual, color: 'info' },
      { name: 'Special Events', amount: results.eventsAnnual, color: 'danger' }
    ];

    categories.forEach(category => {
      const percentage = ((category.amount / results.totalAnnualBudget) * 100).toFixed(1);
      const breakdownItem = `
        <div class="card border-start border-4 border-${category.color} mb-3">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="card-title mb-1">${category.name}</h6>
                <small class="text-muted">${percentage}% of total budget</small>
              </div>
              <div class="text-end">
                <div class="fs-5 fw-bold text-${category.color}">$${category.amount.toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>
      `;
      breakdownList.append(breakdownItem);
    });

    // Update summary values
    $('#total-annual-budget').text(`$${results.totalAnnualBudget.toLocaleString()}`);
    $('#per-employee-budget').text(`$${results.perEmployeeBudget.toLocaleString()}`);
    $('#monthly-budget').text(`$${results.monthlyBudget.toLocaleString()}`);
    
    const budgetVsTargetElement = $('#budget-vs-target');
    const budgetVsTargetText = `$${Math.abs(results.budgetDifference).toLocaleString()}`;
    if (results.budgetDifference > 0) {
      budgetVsTargetElement.text(`+${budgetVsTargetText} over`);
      budgetVsTargetElement.removeClass('text-success text-danger').addClass('text-danger');
    } else if (results.budgetDifference < 0) {
      budgetVsTargetElement.text(`${budgetVsTargetText} under`);
      budgetVsTargetElement.removeClass('text-success text-danger').addClass('text-success');
    } else {
      budgetVsTargetElement.text('On target');
      budgetVsTargetElement.removeClass('text-success text-danger').addClass('text-success');
    }
  }

  // ---------- Charts ----------
  ensureCharts() {
    if (!this.charts.pie) this.createPieChart();
    if (!this.charts.bar) this.createBarChart();
  }

  createPieChart() {
    if (typeof Highcharts === 'undefined') return;
    this.charts.pie = Highcharts.chart('engagement-budget-pie-chart', {
      chart: { type: 'pie' },
      title: { text: null },
      credits: { enabled: false },
      tooltip: { pointFormat: '<b>${point.y:,.0f}</b> ({point.percentage:.1f}%)' },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: { enabled: true, format: '{point.name}: {point.percentage:.1f}%' }
        }
      },
      series: [{ name: 'Annual', colorByPoint: true, data: [] }]
    });
  }

  createBarChart() {
    if (typeof Highcharts === 'undefined') return;
    this.charts.bar = Highcharts.chart('engagement-budget-bar-chart', {
      chart: { type: 'column' },
      title: { text: null },
      credits: { enabled: false },
      xAxis: { categories: ['Wellness', 'Recognition', 'Mentorship', 'Events'] },
      yAxis: { title: { text: 'Annual Cost ($)' } },
      legend: { enabled: false },
      tooltip: { pointFormat: '<b>${point.y:,.0f}</b>' },
      series: [{ name: 'Annual', data: [] }]
    });
  }

  updateCharts({ wellnessAnnual, recognitionAnnual, mentorshipAnnual, eventsAnnual, totalAnnualBudget }) {
    this.ensureCharts();
    const pieData = [
      { name: 'Wellness', y: wellnessAnnual },
      { name: 'Recognition', y: recognitionAnnual },
      { name: 'Mentorship', y: mentorshipAnnual },
      { name: 'Events', y: eventsAnnual }
    ];
    if (this.charts.pie) {
      this.charts.pie.series[0].setData(pieData, true);
    }
    if (this.charts.bar) {
      this.charts.bar.series[0].setData([
        wellnessAnnual,
        recognitionAnnual,
        mentorshipAnnual,
        eventsAnnual
      ], true);
    }
  }

  resetCalculator() {
    $('#engagement-budget-form')[0].reset();
    $('#results-section').hide();
    this.setDefaultValues();
  }
}

export default EmployeeEngagementBudgetCalculator;
